import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';

const DISCORD_CLIENT_ID = '1427814860548210699'; // Your Discord app client ID
const DISCORD_REDIRECT_URI = 'https://guwcqpytunuquoirejsc.supabase.co/functions/v1/discord-oauth-callback';

export const LinkDiscord: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [discordUsername, setDiscordUsername] = useState<string | null>(null);
  const [currentDiscordId, setCurrentDiscordId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlinking, setUnlinking] = useState(false);

  useEffect(() => {
    loadDiscordInfo();
    
    // Check for success parameter
    if (searchParams.get('success') === 'true') {
      toast.success('Discord linked successfully! 🎉');
      // Remove success param from URL
      navigate('/link-discord', { replace: true });
    }
  }, [user, searchParams]);

  const loadDiscordInfo = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('discord_id, discord_username')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setCurrentDiscordId(data.discord_id);
        setDiscordUsername(data.discord_username);
      }
    } catch (error) {
      console.error('Error loading Discord info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectDiscord = () => {
    if (!user) return;

    // Build Discord OAuth URL
    const oauthUrl = new URL('https://discord.com/api/oauth2/authorize');
    oauthUrl.searchParams.set('client_id', DISCORD_CLIENT_ID);
    oauthUrl.searchParams.set('redirect_uri', DISCORD_REDIRECT_URI);
    oauthUrl.searchParams.set('response_type', 'code');
    oauthUrl.searchParams.set('scope', 'identify');
    oauthUrl.searchParams.set('state', user.id); // Pass user ID as state

    // Redirect to Discord OAuth
    window.location.href = oauthUrl.toString();
  };

  const handleUnlink = async () => {
    if (!user) return;

    setUnlinking(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ discord_id: null, discord_username: null })
        .eq('id', user.id);

      if (error) throw error;

      setCurrentDiscordId(null);
      setDiscordUsername(null);
      toast.success('Discord unlinked successfully');
      localStorage.removeItem('discord-banner-dismissed');
    } catch (error) {
      console.error('Error unlinking Discord:', error);
      toast.error('Failed to unlink Discord');
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto p-6">
          <div className="space-y-4 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">Discord Notifications</h1>
          <p className="text-muted-foreground">
            Link your Discord account to receive instant DM notifications for likes, comments, follows, and more.
          </p>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-3">
          {currentDiscordId ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <span className="text-green-600 dark:text-green-400 font-semibold block">
                  Discord Linked
                </span>
                {discordUsername && (
                  <span className="text-sm text-muted-foreground">
                    Connected as @{discordUsername}
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              <XCircle className="h-5 w-5 text-gray-400" />
              <span className="text-gray-500 dark:text-gray-400">
                Discord Not Linked
              </span>
            </>
          )}
        </div>

        {/* Benefits Box */}
        <div className="bg-[#5865F2]/10 dark:bg-[#5865F2]/20 border-2 border-[#5865F2]/40 dark:border-[#5865F2]/50 rounded-lg p-6 space-y-4">
          <h3 className="font-bold text-lg flex items-center gap-2 text-white">
            💬 Get Instant Notifications on Discord!
          </h3>
          <p className="text-sm text-white font-semibold">
            Receive Discord DMs for:
          </p>
          <ul className="space-y-2 text-sm text-white font-medium">
            <li className="flex items-center gap-2">
              <span className="text-lg">❤️</span>
              <span>Likes on your posts</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-lg">💬</span>
              <span>Comments on your posts</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-lg">👤</span>
              <span>New followers</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-lg">🔄</span>
              <span>Reblogs of your content</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-lg">📢</span>
              <span>Mentions</span>
            </li>
          </ul>
          <p className="text-sm text-white italic font-semibold">
            No mobile app needed - Discord handles all your push notifications!
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          {!currentDiscordId ? (
            <Button
              onClick={handleConnectDiscord}
              className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white"
              size="lg"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Connect with Discord
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleUnlink}
              disabled={unlinking}
              size="lg"
              className="w-full"
            >
              {unlinking ? 'Unlinking...' : 'Unlink Discord'}
            </Button>
          )}
        </div>

        {/* Privacy Note */}
        <p className="text-xs text-muted-foreground text-center">
          Your Discord ID is only used to send you notifications. We never share it with anyone.
        </p>

        {/* Mirrorman Mascot */}
        <div className="flex justify-center pt-4">
          <img 
            src="https://guwcqpytunuquoirejsc.supabase.co/storage/v1/object/public/fomo-assets/mirrorman_intro.png" 
            alt="Mirrorman" 
            className="w-32 h-32 object-contain opacity-60"
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default LinkDiscord;
